import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { ImageUploader } from '../components/ImageUploader';
import { ImageViewer } from '../components/ImageViewer';
import { LoginModal } from '../components/LoginModal';
import { GalleryModal } from '../components/GalleryModal';
import { useAuth } from '../contexts/AuthContext';
import { generateHeadshot } from '../services/geminiService';
import { updateCreditsAndSaveImage, getShowcaseItemById } from '../services/firebase';
import {
  clearInheritedStyle,
  loadInheritedStyle,
  normalizeInheritedSettings,
  saveInheritedStyle,
} from '../utils/showcaseStyle';
import {
  PORTRAIT_BASE_PROMPT,
  PORTRAIT_PROMPT_PLACEHOLDER,
  buildPortraitPrompt,
} from '../constants/portrait';

type Step = 'upload' | 'prompt' | 'generate' | 'result';
type ImageData = { data: string; mimeType: string } | null;

export const PortraitPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userPlan, isPaidUser, isLoading: authLoading, forceSyncCredits: syncCredits } = useAuth();
  const remainingCredits = userPlan?.creditsRest ?? 0;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [originalImage, setOriginalImage] = useState<ImageData>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const currentGenerationIdRef = useRef(0);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inheritStyle = urlParams.get('inheritStyle');
    const showcaseId = urlParams.get('showcaseId');

    if (inheritStyle !== '1' && !showcaseId) {
      return;
    }

    let cancelled = false;

    const loadStyle = async () => {
      try {
        let payload = loadInheritedStyle();

        if ((!payload || !payload.settings) && showcaseId) {
          const item = await getShowcaseItemById(showcaseId);
          if (item) {
            payload = {
              showcaseId: item.id,
              prompt: item.prompt,
              imageUrl: item.imageUrl,
              settings: item.settings || null,
            };
            saveInheritedStyle(payload);
          }
        }

        if (cancelled || !payload) {
          return;
        }

        const settings = normalizeInheritedSettings(payload.settings, payload.prompt);
        if (settings.mode !== 'portrait') {
          const target =
            settings.mode === 'musician'
              ? `/musician?inheritStyle=1${showcaseId ? `&showcaseId=${encodeURIComponent(showcaseId)}` : ''}`
              : `/?mode=app&inheritStyle=1${showcaseId ? `&showcaseId=${encodeURIComponent(showcaseId)}` : ''}`;
          window.location.replace(target);
          return;
        }

        setUserPrompt(settings.userPrompt || '');
        setCurrentStep('upload');

        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        console.error('ポートレートスタイルの引き継ぎに失敗:', error);
      }
    };

    loadStyle();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleImageUpload = (data: string, mimeType: string) => {
    setOriginalImage({ data, mimeType });
    setGeneratedImage(null);
    setError(null);
    setCurrentStep('prompt');
  };

  const handleReset = () => {
    setOriginalImage(null);
    setGeneratedImage(null);
    setError(null);
    setIsGenerating(false);
    setGenerationProgress(0);
    setGenerationStep('');
    setCurrentStep('upload');
    setUserPrompt('');
    setCurrentPrompt('');
  };

  const handleGenerate = async () => {
    if (!originalImage || !user) return;
    if (isGenerating) return;
    if (!userPrompt.trim()) {
      setError('プロンプトを入力してください。');
      return;
    }
    if (remainingCredits <= 0) {
      setError('クレジットが不足しています。有料プランをご検討ください。');
      return;
    }

    const generationId = ++currentGenerationIdRef.current;
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);
    setCurrentStep('generate');
    setGenerationProgress(0);
    setGenerationStep('画像を分析中...');
    await new Promise((r) => setTimeout(r, 100));

    try {
      const finalPrompt = buildPortraitPrompt(userPrompt);
      setCurrentPrompt(finalPrompt);

      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        throw new Error('ネットワーク接続がありません。');
      }

      const result = await generateHeadshot(
        originalImage.data,
        originalImage.mimeType,
        finalPrompt,
        null,
        null
      );

      if (generationId !== currentGenerationIdRef.current) return;
      if (!result) throw new Error('画像生成に失敗しました');

      setGeneratedImage(result);
      setCurrentStep('result');

      if (user) {
        try {
          let finalImageForSave = result;
          if (!isPaidUser) {
            try {
              const { downscaleImage } = await import('../utils/watermarkUtils');
              finalImageForSave = await downscaleImage(result, 0.5);
            } catch {
              finalImageForSave = result;
            }
          }
          const settings = {
            mode: 'portrait' as const,
            userPrompt: userPrompt.trim(),
            originalImageMimeType: originalImage.mimeType,
          };
          await updateCreditsAndSaveImage(
            user.uid,
            finalImageForSave,
            originalImage.mimeType,
            finalPrompt,
            'portrait',
            undefined,
            undefined,
            undefined,
            settings
          );
          await syncCredits?.();
        } catch (saveErr: unknown) {
          console.warn('保存エラー（画像は表示されます）:', saveErr);
        }
      }
    } catch (e: unknown) {
      if (generationId !== currentGenerationIdRef.current) return;
      const msg = e instanceof Error ? e.message : '画像生成中にエラーが発生しました';
      setError(msg);
      setCurrentStep('prompt');
    } finally {
      if (generationId !== currentGenerationIdRef.current) return;
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  useEffect(() => {
    if (!isGenerating) return;
    const steps = [
      { progress: 20, text: '画像を分析中...' },
      { progress: 40, text: 'AIが生成中...' },
      { progress: 60, text: '画像を処理中...' },
      { progress: 80, text: '最終調整中...' },
      { progress: 95, text: '仕上げ中...' },
    ];
    let i = 0;
    const iv = setInterval(() => {
      if (i < steps.length) {
        setGenerationProgress(steps[i].progress);
        setGenerationStep(steps[i].text);
        i++;
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [isGenerating]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans antialiased flex items-center justify-center">
        <p className="text-slate-600">読み込み中...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white font-sans antialiased">
        <Header onGoHome={() => navigate('/')} variant="portrait" />

        <section className="relative overflow-hidden bg-slate-50 py-20 sm:py-28">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <p className="text-sm font-semibold tracking-wide text-teal-700 mb-3">PORTRAIT MODE</p>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6">
              プロンプト自由入力の<br />
              <span className="text-teal-700">ポートレートモード</span>
            </h1>
            <p className="text-lg leading-8 text-slate-600">
              写真をアップロードし、プロンプトを自由に書いて生成します。<br />
              宣材写真はもちろん、雑誌風ポスターや水彩画調など、表現の幅を広げられます。
            </p>
            <button
              type="button"
              onClick={() => setShowLoginModal(true)}
              className="mt-10 bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-800 transition-colors shadow-lg"
            >
              ログインして始める
            </button>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-white">
          <div className="mx-auto max-w-5xl px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">このモードでできること</h2>
            </div>
            <dl className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div>
                <dt className="font-semibold text-slate-900 mb-2">フリープロンプト</dt>
                <dd className="text-sm text-slate-600">構図・照明・画風まで、英語プロンプトで細かく指定できます。</dd>
              </div>
              <div>
                <dt className="font-semibold text-slate-900 mb-2">本人性を保持</dt>
                <dd className="text-sm text-slate-600">顔・年齢感・髪型などが大きく変わらないよう、固定プロンプトを自動付与します。</dd>
              </div>
              <div>
                <dt className="font-semibold text-slate-900 mb-2">表現の幅</dt>
                <dd className="text-sm text-slate-600">スタジオ宣材からエディトリアル、イラスト調まで同じ流れで作成できます。</dd>
              </div>
            </dl>
          </div>
        </section>

        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => setShowLoginModal(false)}
        />
      </div>
    );
  }

  const steps: { step: Step; label: string }[] = [
    { step: 'upload', label: '写真を選ぶ' },
    { step: 'prompt', label: 'プロンプト' },
    { step: 'generate', label: '生成中' },
    { step: 'result', label: '結果' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased">
      <Header
        onGoHome={() => navigate('/')}
        onShowGallery={() => setShowGalleryModal(true)}
        showTrialInfo={!isPaidUser}
        trialRemaining={remainingCredits}
        userPlan={userPlan}
        variant="portrait"
      />
      <main className="container mx-auto px-4 md:px-8 pt-20 pb-6 md:pb-12">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">ポートレートモード</h1>
          <p className="text-slate-600 text-sm">
            写真を添付し、プロンプトを自由入力して生成するプロ向けモードです
          </p>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 justify-center">
            {steps.map(({ step, label }, idx) => (
              <div key={step} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === step ? 'bg-teal-700 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {idx + 1}
                </div>
                <span className="text-sm font-medium text-slate-600">{label}</span>
                {idx < steps.length - 1 && (
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>

        {currentStep === 'upload' && (
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-4">ステップ1: 写真を選ぶ</h2>
            <p className="text-slate-600 mb-6">参照となる写真をアップロードしてください</p>
            <ImageUploader
              onImageUpload={handleImageUpload}
              freeTrialUsed={remainingCredits}
              scrollToPlanInfo={() => navigate('/')}
              userPlan={userPlan}
            />
          </div>
        )}

        {currentStep === 'prompt' && originalImage && (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">ステップ2: プロンプト入力</h2>
              <button
                type="button"
                onClick={() => setCurrentStep('upload')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                ← 戻る
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5">
              <div className="flex items-start gap-4 mb-5">
                <img
                  src={originalImage.data}
                  alt="アップロード画像"
                  className="h-24 w-24 rounded-lg object-cover border border-slate-200"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 mb-1">自動で付与される固定プロンプト</p>
                  <p className="text-xs text-slate-500 mb-2">
                    本人の顔・年齢感・髪型などは保ちつつ、服・背景・ポーズは参照写真からコピーしない文言です（編集不可）
                  </p>
                  <pre className="whitespace-pre-wrap rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 leading-relaxed">
                    {PORTRAIT_BASE_PROMPT}
                  </pre>
                </div>
              </div>

              <label htmlFor="portrait-prompt" className="block text-sm font-semibold text-slate-800 mb-2">
                追加プロンプト（自由入力）
              </label>
              <textarea
                id="portrait-prompt"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={14}
                placeholder={PORTRAIT_PROMPT_PLACEHOLDER}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm text-slate-800 leading-relaxed focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-200"
              />
              <p className="mt-2 text-xs text-slate-500">
                ポーズ、背景、照明、画風などを英語で書くと精度が上がります。雑誌風・水彩風なども指定できます。
              </p>

              {error && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isGenerating || !userPrompt.trim() || remainingCredits <= 0}
                  className="flex-1 bg-teal-700 text-white py-3 px-4 rounded-lg font-semibold hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  このプロンプトで生成する
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="sm:w-40 border border-slate-300 text-slate-700 py-3 px-4 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  リセット
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500">残りクレジット: {remainingCredits} 回</p>
            </div>
          </div>
        )}

        {currentStep === 'generate' && (
          <div className="text-center max-w-md mx-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-4">生成中...</h2>
            <div className="bg-slate-100 rounded-lg p-8">
              <p className="font-semibold text-slate-700 mb-2">{generationStep || 'AIが生成しています'}</p>
              <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                <div
                  className="bg-teal-700 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              <p className="text-sm text-slate-500">{generationProgress}%</p>
            </div>
          </div>
        )}

        {currentStep === 'result' && originalImage && (
          <div className="w-full">
            <h2 className="text-xl font-bold text-slate-900 mb-4">生成完了！</h2>
            <p className="text-slate-600 mb-6">生成されたポートレートをご確認ください</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ImageViewer
                originalImage={originalImage.data}
                generatedImage={generatedImage}
                isLoading={false}
                isPaidUser={isPaidUser ?? false}
                currentPrompt={currentPrompt}
              />
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">次のアクション</h3>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full bg-teal-700 text-white py-3 px-4 rounded-lg hover:bg-teal-800"
                  >
                    新しい写真を作成
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      currentGenerationIdRef.current++;
                      setIsGenerating(false);
                      setError(null);
                      setGenerationProgress(0);
                      setGenerationStep('');
                      setCurrentStep('prompt');
                    }}
                    className="w-full border border-teal-700 text-teal-800 py-3 px-4 rounded-lg hover:bg-teal-50"
                  >
                    プロンプトを調整して再生成
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowGalleryModal(true)}
                    className="w-full border border-slate-300 text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-50"
                  >
                    ギャラリーを開く
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <GalleryModal isOpen={showGalleryModal} onClose={() => setShowGalleryModal(false)} />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => setShowLoginModal(false)}
      />
    </div>
  );
};
